export type CanvasPoint = {
  x: number;
  y: number;
};

type EntityLayerName = 'Roof' | 'Fall Protection Systems' | 'Dimensions' | 'Labels' | 'Text';

type BaseEntityMeta = {
  name: string;
  strokeColor: string;
  strokeWidth: number;
  layer?: EntityLayerName;
  entityStyleKind?: string;
  isObstacle?: boolean;
};

type PolylineLikeShape = BaseEntityMeta & {
  id: string;
  points: number[];
  holes?: number[][];
  circleHoles?: Array<{ center: CanvasPoint; radius: number }>;
  closed: boolean;
  kind: 'polyline' | 'rectangle';
};

type CircleShape = BaseEntityMeta & {
  id: string;
  kind: 'circle';
  closed: true;
  center: CanvasPoint;
  radius: number;
};

type ArcShape = BaseEntityMeta & {
  id: string;
  kind: 'arc';
  closed: false;
  center: CanvasPoint;
  radius: number;
  startAngle: number;
  endAngle: number;
};

export type Shape = PolylineLikeShape | CircleShape | ArcShape;

export type AutoDimensionDefinition = {
  start: CanvasPoint;
  end: CanvasPoint;
  offset: number;
  dimensionType: 'linear' | 'radius';
};

export type DimensionLayout = {
  ux: number;
  uy: number;
  nx: number;
  ny: number;
  angleDeg: number;
  textRotation: number;
  extensionStart: CanvasPoint;
  extensionStartOffset: CanvasPoint;
  extensionEnd: CanvasPoint;
  extensionEndOffset: CanvasPoint;
  lineMidPoint: CanvasPoint;
  textPoint: CanvasPoint;
};

const getMidPoint = (pointA: CanvasPoint, pointB: CanvasPoint) => ({
  x: (pointA.x + pointB.x) / 2,
  y: (pointA.y + pointB.y) / 2,
});

const getPolylineVertices = (points: number[]) => {
  const vertices: CanvasPoint[] = [];

  for (let index = 0; index < points.length - 1; index += 2) {
    const x = points[index];
    const y = points[index + 1];
    if (x == null || y == null) continue;

    const nextVertex = {
      x,
      y,
    };
    const previousVertex = vertices[vertices.length - 1];

    if (
      previousVertex &&
      Math.abs(previousVertex.x - nextVertex.x) <= 0.0001 &&
      Math.abs(previousVertex.y - nextVertex.y) <= 0.0001
    ) {
      continue;
    }

    vertices.push(nextVertex);
  }

  return vertices;
};

const getPolylineCenter = (points: number[]) => {
  if (points.length < 4) return null;

  let minX = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;

  for (let index = 0; index < points.length - 1; index += 2) {
    const x = points[index];
    const y = points[index + 1];
    if (x == null || y == null) continue;

    minX = Math.min(minX, x);
    maxX = Math.max(maxX, x);
    minY = Math.min(minY, y);
    maxY = Math.max(maxY, y);
  }

  return {
    center: {
      x: (minX + maxX) / 2,
      y: (minY + maxY) / 2,
    },
  };
};

const getPolylineSignedArea = (points: number[]) => {
  let area = 0;

  for (let index = 0; index < points.length - 1; index += 2) {
    const nextIndex = (index + 2) % points.length;
    const x = points[index];
    const y = points[index + 1];
    const nextX = points[nextIndex];
    const nextY = points[nextIndex + 1];
    if (x == null || y == null || nextX == null || nextY == null) continue;

    area += x * nextY - nextX * y;
  }

  return area / 2;
};

const getShapeReferenceCenter = (shape: Shape) => {
  if (shape.kind === 'circle' || shape.kind === 'arc') {
    return { x: shape.center.x, y: shape.center.y };
  }

  const bounds = getPolylineCenter(shape.points);
  return bounds?.center ?? getPolylineVertices(shape.points)[0] ?? { x: 0, y: 0 };
};

export const getDimensionLayout = (
  start: CanvasPoint,
  end: CanvasPoint,
  offset: number,
  textGap: number,
): DimensionLayout | null => {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const length = Math.hypot(dx, dy);
  if (length === 0) return null;

  const ux = dx / length;
  const uy = dy / length;
  const nx = -uy;
  const ny = ux;
  const angleDeg = (Math.atan2(dy, dx) * 180) / Math.PI;
  const normalizedAngle = angleDeg > 90 || angleDeg < -90 ? angleDeg + 180 : angleDeg;
  const textRotation =
    Math.abs(Math.abs(normalizedAngle) - 90) < 0.001 ? -90 : normalizedAngle;
  const isHorizontalLike = normalizedAngle >= -45 && normalizedAngle <= 45;

  const dimensionStart = {
    x: start.x + nx * offset,
    y: start.y + ny * offset,
  };
  const dimensionEnd = {
    x: end.x + nx * offset,
    y: end.y + ny * offset,
  };

  return {
    ux,
    uy,
    nx,
    ny,
    angleDeg,
    textRotation,
    extensionStart: start,
    extensionStartOffset: dimensionStart,
    extensionEnd: end,
    extensionEndOffset: dimensionEnd,
    lineMidPoint: {
      x: (dimensionStart.x + dimensionEnd.x) / 2,
      y: (dimensionStart.y + dimensionEnd.y) / 2,
    },
    textPoint: {
      x:
        (dimensionStart.x + dimensionEnd.x) / 2 +
        (isHorizontalLike ? 0 : nx * textGap),
      y:
        (dimensionStart.y + dimensionEnd.y) / 2 +
        (isHorizontalLike ? -textGap : ny * textGap),
    },
  };
};

export const getArrowHeadPoints = (
  tip: CanvasPoint,
  direction: CanvasPoint,
  size: number,
  width: number,
) => {
  const base = {
    x: tip.x + direction.x * size,
    y: tip.y + direction.y * size,
  };
  const normal = {
    x: -direction.y,
    y: direction.x,
  };

  return [
    tip.x,
    tip.y,
    base.x + normal.x * width,
    base.y + normal.y * width,
    base.x - normal.x * width,
    base.y - normal.y * width,
  ];
};

export const getSlashMarkerPoints = (
  tip: CanvasPoint,
  direction: CanvasPoint,
  size: number,
) => {
  const normal = {
    x: -direction.y,
    y: direction.x,
  };
  const half = size / 2;

  return [
    tip.x - normal.x * half - direction.x * half,
    tip.y - normal.y * half - direction.y * half,
    tip.x + normal.x * half + direction.x * half,
    tip.y + normal.y * half + direction.y * half,
  ];
};

export const getDimensionOffsetFromPoint = (
  start: CanvasPoint,
  end: CanvasPoint,
  point: CanvasPoint,
) => {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const length = Math.hypot(dx, dy);
  if (length === 0) return 0;

  const nx = -dy / length;
  const ny = dx / length;
  return (point.x - start.x) * nx + (point.y - start.y) * ny;
};

const buildAutoDimensionPointer = (
  start: CanvasPoint,
  end: CanvasPoint,
  shape: Shape,
  offsetDistance: number,
) => {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const length = Math.hypot(dx, dy);
  if (length === 0) return null;

  const midpoint = getMidPoint(start, end);
  const leftNormal = { x: -dy / length, y: dx / length };

  if (shape.kind !== 'circle' && shape.kind !== 'arc' && shape.closed) {
    const areaSign = getPolylineSignedArea(shape.points) >= 0 ? 1 : -1;
    return {
      x: midpoint.x - leftNormal.x * areaSign * offsetDistance,
      y: midpoint.y - leftNormal.y * areaSign * offsetDistance,
    };
  }

  const center = getShapeReferenceCenter(shape);
  const centerVector = {
    x: midpoint.x - center.x,
    y: midpoint.y - center.y,
  };
  const dot = leftNormal.x * centerVector.x + leftNormal.y * centerVector.y;
  const outwardNormal =
    dot >= 0
      ? leftNormal
      : {
          x: -leftNormal.x,
          y: -leftNormal.y,
        };

  return {
    x: midpoint.x + outwardNormal.x * offsetDistance,
    y: midpoint.y + outwardNormal.y * offsetDistance,
  };
};

export const buildAutoDimensionsForShape = (
  shape: Shape,
  offsetDistance: number,
): AutoDimensionDefinition[] => {
  if (shape.kind === 'circle') {
    const start = { x: shape.center.x, y: shape.center.y };
    const end = {
      x: shape.center.x + shape.radius,
      y: shape.center.y,
    };
    const pointer = {
      x: shape.center.x,
      y: shape.center.y - shape.radius - offsetDistance,
    };
    const offset = getDimensionOffsetFromPoint(start, end, pointer);
    return [{ start, end, offset, dimensionType: 'radius' }];
  }

  if (shape.kind === 'arc') {
    const start = { x: shape.center.x, y: shape.center.y };
    const end = {
      x: shape.center.x + Math.cos(shape.startAngle) * shape.radius,
      y: shape.center.y + Math.sin(shape.startAngle) * shape.radius,
    };
    const pointer = {
      x: shape.center.x,
      y: shape.center.y - shape.radius - offsetDistance,
    };
    const offset = getDimensionOffsetFromPoint(start, end, pointer);
    return [{ start, end, offset, dimensionType: 'radius' }];
  }

  const vertices = getPolylineVertices(shape.points);
  if (vertices.length < 2) return [];

  const segments: Array<{ start: CanvasPoint; end: CanvasPoint }> = [];
  for (let index = 1; index < vertices.length; index += 1) {
    segments.push({
      start: vertices[index - 1]!,
      end: vertices[index]!,
    });
  }

  if (shape.closed && vertices.length >= 3) {
    segments.push({
      start: vertices[vertices.length - 1]!,
      end: vertices[0]!,
    });
  }

  const definitions: Array<AutoDimensionDefinition | null> = segments.map((segment) => {
      const pointer = buildAutoDimensionPointer(
        segment.start,
        segment.end,
        shape,
        offsetDistance,
      );
      if (!pointer) return null;

      return {
        start: segment.start,
        end: segment.end,
        offset: getDimensionOffsetFromPoint(segment.start, segment.end, pointer),
        dimensionType: 'linear' as const,
      };
    });

  return definitions.filter((dimension): dimension is AutoDimensionDefinition => dimension != null);
};
